import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    clearSidebarStateForViewport,
    closeMobileSidebar,
    ensureSidebarOverlay,
    toggleMiniSidebar,
} from './layoutUtils'
import { getStoredUser, getUserDisplayName, signOut } from '../../config/authUtils'

const Sidebar = () => {
    const location = useLocation()
    const navigate = useNavigate()

    const isActive = (path) => location.pathname === path

    const [openSubmenus, setOpenSubmenus] = useState({
        costCenters: location.pathname.startsWith('/hr/cost-centers'),
        departments: location.pathname.startsWith('/hr/departments'),
        employees: location.pathname.startsWith('/hr/employees') || location.pathname.startsWith('/hr/department-employees'),
        kra: location.pathname.startsWith('/hr/key-result-areas'),
        kpis: location.pathname.startsWith('/hr/kpis'),
        improvementPlans: location.pathname.startsWith('/hr/improvement-plans'),
    })

    useEffect(() => {
        setOpenSubmenus({
            costCenters: location.pathname.startsWith('/hr/cost-centers'),
            departments: location.pathname.startsWith('/hr/departments'),
            employees: location.pathname.startsWith('/hr/employees') || location.pathname.startsWith('/hr/department-employees'),
            kra: location.pathname.startsWith('/hr/key-result-areas'),
            kpis: location.pathname.startsWith('/hr/kpis'),
            improvementPlans: location.pathname.startsWith('/hr/improvement-plans'),
        })
    }, [location.pathname])

    const toggleSubmenu = (menuKey) => {
        setOpenSubmenus((prev) => ({
            ...prev,
            [menuKey]: !prev[menuKey],
        }))
    }

    useEffect(() => {
        const overlay = ensureSidebarOverlay()
        const handleOverlayClick = () => closeMobileSidebar()
        overlay?.addEventListener('click', handleOverlayClick)
        return () => overlay?.removeEventListener('click', handleOverlayClick)
    }, [])

    useEffect(() => {
        clearSidebarStateForViewport()
        let resizeTimeout
        const handleResize = () => {
            clearTimeout(resizeTimeout)
            resizeTimeout = setTimeout(clearSidebarStateForViewport, 100)
        }
        window.addEventListener('resize', handleResize)
        return () => {
            clearTimeout(resizeTimeout)
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        closeMobileSidebar()
    }, [location.pathname])

    const storedUser = getStoredUser()
    console.log("Stored User:", storedUser)

    const handleLogout = () => {
        signOut()
        closeMobileSidebar()
        navigate('/lockscreen', { replace: true })
    }

    const navLinkClass = (path) => (isActive(path) ? 'active' : '')

    return (
        <div className="main-wrapper">
            <div className="sidebar shadow-sm" id="sidebar" style={{ borderRight: '1px solid rgba(0,0,0,0.05)', backgroundColor: 'var(--bs-body-bg)' }}>
                <div className="sidebar-logo border-bottom" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                    <div>
                        <Link to="/" className="logo logo-normal mt-3">
                            <img style={{ height: '80px', width: '150px', objectFit: 'contain' }} src="assets/img/media/celebration-dark.png" alt="Logo" />
                        </Link>
                        <Link to="/" className="logo-small">
                            <img style={{ height: '65px', width: '150px', objectFit: 'contain' }} src="assets/img/media/celebration-dark.png" alt="Logo" />
                        </Link>
                        <Link to="/" className="dark-logo">
                            <img src="assets/img/media/celebration-light.png" alt="Logo" />
                        </Link>
                    </div>
                    <button
                        type="button"
                        className="sidenav-toggle-btn btn p-0"
                        id="toggle_btn"
                        onClick={toggleMiniSidebar}
                        aria-label="Toggle sidebar"
                    >
                        <i className="ti ti-chevron-left-pipe" />
                    </button>
                    <button
                        type="button"
                        className="sidebar-close"
                        onClick={closeMobileSidebar}
                        aria-label="Close sidebar"
                    >
                        <i className="ti ti-x align-middle" />
                    </button>
                </div>

                <div
                    className="sidebar-inner"
                    style={{
                        overflowY: 'auto',
                        height: 'calc(100vh - 60px)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div id="sidebar-menu" className="sidebar-menu flex-grow-1">
                        <ul className="pt-1">
                            <li className="menu-title"><span className="fw-semibold text-uppercase tracking-wider opacity-75">Personal</span></li>
                            <li>
                                <ul>
                                    <li>
                                        <Link to="/" className={navLinkClass('/')}>
                                            <i className="ti ti-layout-dashboard" /><span>Dashboard</span>
                                        </Link>
                                    </li>
                                    <li className="submenu">
                                        <Link
                                            to="#"
                                            className={`${location.pathname.startsWith('/hr/key-result-areas') ? 'active' : ''} ${openSubmenus.kra ? 'subdrop' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                toggleSubmenu('kra')
                                            }}
                                        >
                                            <i className="ti ti-target" /><span>Key Result Areas</span><span className="menu-arrow" />
                                        </Link>
                                        <ul style={{ display: openSubmenus.kra ? 'block' : 'none' }}>
                                            <li>
                                                <Link to="/employee/kras/add" className={navLinkClass('/employee/kras/add')}>
                                                    <span>Add Key Result Area</span>
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/employee/kras" className={navLinkClass('/employee/kras')}>
                                                    <span>Key Result Areas</span>
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>

                                    <li className="submenu">
                                        <Link
                                            to="#"
                                            className={`${location.pathname.startsWith('/employee/kpis') ? 'active' : ''} ${openSubmenus.kpis ? 'subdrop' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                toggleSubmenu('kpis')
                                            }}
                                        >
                                            <i className="ti ti-chart-bar" /><span>My KPIs</span><span className="menu-arrow" />
                                        </Link>
                                        <ul style={{ display: openSubmenus.kpis ? 'block' : 'none' }}>
                                            <li>
                                                <Link to="/employee/kpis/add" className={navLinkClass('/employee/kpis/add')}>
                                                    <span>Add KPI</span>
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/employee/kpis" className={navLinkClass('/employee/kpis')}>
                                                    <span>My KPIs</span>
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>

                                    <li>
                                        <Link to="/employee/kpi-reviwes" className={navLinkClass('/employee/kpi-reviwes')}>
                                            <i className="ti ti-chart-line" /><span>KPI Reviews</span>
                                        </Link>
                                    </li>

                                    <li className="submenu">
                                        <Link
                                            to="#"
                                            className={`${location.pathname.startsWith('/employee/improvement-plans') ? 'active' : ''} ${openSubmenus.improvementPlans ? 'subdrop' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                toggleSubmenu('improvementPlans')
                                            }}
                                        >
                                            <i className="ti ti-school" /><span>Improvement Plans</span><span className="menu-arrow" />
                                        </Link>
                                        <ul style={{ display: openSubmenus.improvementPlans ? 'block' : 'none' }}>
                                            <li>
                                                <Link to="/employee/improvement-plans/add" className={navLinkClass('/employee/improvement-plans/add')}>
                                                    <span>Add Improvement Plan</span>
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/employee/improvement-plans" className={navLinkClass('/employee/improvement-plans')}>
                                                    <span>Improvement Plans</span>
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>

                                    <li>
                                        <Link to="/employee/change-password" className={navLinkClass('/employee/change-password')}>
                                            <i className="ti ti-lock" /><span>Change Password</span>
                                        </Link>
                                    </li>

                                    <li>
                                        <Link to="/employee/two-factor-authentication" className={navLinkClass('/employee/two-factor-authentication')}>
                                            <i className="ti ti-shield-lock" /><span>Two-Factor Auth</span>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>

                    <div className="sidebar-footer mt-auto p-3 border-top" style={{ borderColor: 'rgba(0,0,0,0.05)', backgroundColor: 'var(--bs-body-bg)' }}>
                        <div className="p-2 mb-3 rounded-3 d-flex align-items-center" style={{ backgroundColor: 'var(--bs-light)', border: '1px solid rgba(0,0,0,0.05)', transition: 'all 0.3s ease' }}>
                            <span className="avatar avatar-md me-3 shadow-sm rounded-circle border border-white">
                                <img src="/assets/img/avatar101.jpg" alt="" className="rounded-circle" />
                            </span>
                            <div className="overflow-hidden">
                                <h6 className="fs-14 fw-bold mb-0 text-truncate">{getUserDisplayName(storedUser)}</h6>
                                <p className="fs-12 text-muted mb-0 text-truncate">{storedUser?.role_display || storedUser?.email || '—'}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn w-100 d-flex align-items-center justify-content-center fw-medium shadow-sm transition-all"
                            onClick={handleLogout}
                            style={{ backgroundColor: '#fff0f0', color: '#dc3545', border: '1px solid #ffdcdc' }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dc3545'; e.currentTarget.style.color = '#fff' }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff0f0'; e.currentTarget.style.color = '#dc3545' }}
                        >
                            <i className="ti ti-logout-2 me-2 fs-16" />Logout
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Sidebar

